var __defProp = Object.defineProperty;
var __name = (target, value) => __defProp(target, "name", { value, configurable: true });

// src/split-page.tsx
import React3 from "react";
import { DataTable, SearchInput, FilterBar } from "@repo/web-list";

// src/split-drawer.tsx
import React2 from "react";

// src/drawer-ui.tsx
import React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cn } from "@repo/utils";
var Drawer = DialogPrimitive.Root;
var DrawerPortal = DialogPrimitive.Portal;
var DrawerClose = DialogPrimitive.Close;
var DrawerOverlay = /* @__PURE__ */ React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ React.createElement(DialogPrimitive.Overlay, {
  ref,
  className: cn("fixed inset-0 z-overlay bg-black/50 duration-slow data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0", className),
  ...props
}));
DrawerOverlay.displayName = "DrawerOverlay";
var DrawerContent = /* @__PURE__ */ React.forwardRef(({ className, children, ...props }, ref) => /* @__PURE__ */ React.createElement(DrawerPortal, null, /* @__PURE__ */ React.createElement(DrawerOverlay, null), /* @__PURE__ */ React.createElement(DialogPrimitive.Content, {
  ref,
  className: cn("fixed inset-y-0 right-0 z-modal flex flex-col h-full bg-surface-1 border-l border-border shadow-2xl duration-slow data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:slide-in-from-right-full data-[state=closed]:slide-out-to-right-full", className),
  ...props
}, children)));
DrawerContent.displayName = "DrawerContent";
var DrawerHeader = /* @__PURE__ */ __name(({ className, ...props }) => /* @__PURE__ */ React.createElement("div", {
  className: cn("px-6 py-5 bg-surface-2 border-b border-border flex items-center justify-between flex-shrink-0", className),
  ...props
}), "DrawerHeader");
DrawerHeader.displayName = "DrawerHeader";
var DrawerTitle = /* @__PURE__ */ React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ React.createElement(DialogPrimitive.Title, {
  ref,
  className: cn("text-lg font-bold text-text-primary", className),
  ...props
}));
DrawerTitle.displayName = "DrawerTitle";
var DrawerDescription = /* @__PURE__ */ React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ React.createElement(DialogPrimitive.Description, {
  ref,
  className: cn("text-sm text-text-muted", className),
  ...props
}));
DrawerDescription.displayName = "DrawerDescription";
var DrawerBody = /* @__PURE__ */ __name(({ className, ...props }) => /* @__PURE__ */ React.createElement("div", {
  className: cn("flex-1 overflow-y-auto p-6 space-y-6", className),
  ...props
}), "DrawerBody");
DrawerBody.displayName = "DrawerBody";
var DrawerFooter = /* @__PURE__ */ __name(({ className, ...props }) => /* @__PURE__ */ React.createElement("div", {
  className: cn("px-6 py-4 border-t border-border bg-surface-2 flex justify-end gap-3 flex-shrink-0", className),
  ...props
}), "DrawerFooter");
DrawerFooter.displayName = "DrawerFooter";

// src/split-drawer.tsx
function SplitDrawer({ isOpen, onClose, title, isLoading, isError, children, footer }) {
  return /* @__PURE__ */ React2.createElement(Drawer, {
    open: isOpen,
    onOpenChange: /* @__PURE__ */ __name((open) => {
      if (!open) onClose();
    }, "onOpenChange")
  }, /* @__PURE__ */ React2.createElement(DrawerContent, null, /* @__PURE__ */ React2.createElement(DrawerHeader, null, /* @__PURE__ */ React2.createElement(DrawerTitle, null, title), /* @__PURE__ */ React2.createElement(DrawerClose, {
    className: "text-text-secondary hover:text-text-primary p-1 rounded-full hover:bg-surface-3 transition-colors",
    "aria-label": "Close"
  }, /* @__PURE__ */ React2.createElement("svg", {
    className: "h-5 w-5",
    fill: "none",
    viewBox: "0 0 24 24",
    strokeWidth: "1.5",
    stroke: "currentColor"
  }, /* @__PURE__ */ React2.createElement("path", {
    strokeLinecap: "round",
    strokeLinejoin: "round",
    d: "M6 18L18 6M6 6l12 12"
  })))), /* @__PURE__ */ React2.createElement(DrawerBody, null, isLoading && /* @__PURE__ */ React2.createElement("div", {
    className: "flex items-center justify-center h-48 text-text-muted text-sm"
  }, "Loading details\u2026"), isError && /* @__PURE__ */ React2.createElement("div", {
    className: "p-4 bg-danger-bg text-danger-text rounded-lg text-sm"
  }, "Failed to load details."), !isLoading && !isError && children), footer && /* @__PURE__ */ React2.createElement(DrawerFooter, null, footer)));
}
__name(SplitDrawer, "SplitDrawer");

// src/split-page.tsx
function SplitPage({ config, list, title, subtitle, customRenderers, detailPane }) {
  const filters = config.listConfig.filters ?? [];
  const detailTitle = `${title} Detail`;
  return /* @__PURE__ */ React3.createElement("div", {
    className: "flex flex-col w-full h-full",
    style: {
      viewTransitionName: "page-body"
    }
  }, /* @__PURE__ */ React3.createElement("div", {
    className: "mb-4"
  }, /* @__PURE__ */ React3.createElement("h1", {
    className: "text-2xl font-bold text-text-primary"
  }, title), subtitle && /* @__PURE__ */ React3.createElement("p", {
    className: "text-sm text-text-secondary"
  }, subtitle)), /* @__PURE__ */ React3.createElement("div", {
    className: "flex flex-wrap items-center gap-3 mb-4"
  }, config.listConfig.searchable && /* @__PURE__ */ React3.createElement(SearchInput, {
    value: list.search,
    onChange: list.setSearch
  }), /* @__PURE__ */ React3.createElement(FilterBar, {
    filters,
    values: list.filters,
    onChange: list.setFilter,
    onReset: list.resetFilters
  })), /* @__PURE__ */ React3.createElement(DataTable, {
    config: config.listConfig,
    list,
    customRenderers
  }), /* @__PURE__ */ React3.createElement(SplitDrawer, {
    isOpen: list.selectedId !== null,
    onClose: list.closeDrawer,
    title: detailTitle,
    isLoading: list.isDetailLoading,
    isError: list.isDetailError
  }, typeof detailPane === "function" ? detailPane(list.detailData) : detailPane));
}
__name(SplitPage, "SplitPage");
export {
  SplitDrawer,
  SplitPage
};
//# sourceMappingURL=index.js.map